import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAtomValue } from 'jotai';

import type {
	IInventoryItem,
	InventoryDataSource,
	InventoryItemSearchRecord,
	PaginatedResponse,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { inventoryEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

import { inventoryParamsAtom } from '../atoms/inventoryAtoms';

/**
 * Input type for patching an inventory item
 */
export interface PatchInventoryItemInput {
	id: string;
	updates: Partial<IInventoryItem> & {
		source?: InventoryDataSource;
	};
}

/**
 * Internal input type that includes servicesProviderId for the API call
 */
interface PatchInventoryItemMutationInput extends PatchInventoryItemInput {
	servicesProviderId: string;
}

/**
 * API function to update an inventory item's information
 */
const patchInventoryItemApi = async ({
	servicesProviderId,
	id,
	updates,
}: PatchInventoryItemMutationInput): Promise<IInventoryItem> => {
	const response = await apiClient.patch<IInventoryItem>(
		inventoryEndpoints.detail(servicesProviderId, id),
		updates,
	);
	return response.data;
};

/**
 * Hook for updating an inventory item's information
 */
export const usePatchInventoryItem = () => {
	const queryClient = useQueryClient();
	const currentServicesProviderId = useCurrentServicesProviderId();
	const params = useAtomValue(inventoryParamsAtom);

	return useMutation({
		mutationFn: (input: PatchInventoryItemInput) =>
			patchInventoryItemApi({
				...input,
				servicesProviderId: currentServicesProviderId,
			}),
		onMutate: async ({ id, updates }) => {
			if (!currentServicesProviderId) return;

			const queryKey = queries.inventory.list(currentServicesProviderId, params).queryKey;

			// Cancel any outgoing refetches to avoid overwriting optimistic update
			await queryClient.cancelQueries({ queryKey });

			// Snapshot the previous value for rollback
			const previousData = queryClient.getQueryData<PaginatedResponse<InventoryItemSearchRecord>>(
				queryKey,
			);

			// Optimistically update the list cache
			queryClient.setQueryData<PaginatedResponse<InventoryItemSearchRecord>>(
				queryKey,
				(oldData) => {
					if (!oldData) return oldData;

					const { data: items, ...rest } = oldData;

					// Find and update the item in the list
					const updatedItems = items.map((item) => {
						if (item.id === id) {
							// Merge updates into the existing item
							return {
								...item,
								...updates,
							} as InventoryItemSearchRecord;
						}
						return item;
					});

					return {
						...rest,
						data: updatedItems,
					};
				},
			);

			// Also optimistically update the detail cache
			const detailQueryKey = queries.inventory.detail(id).queryKey;
			const previousDetailData = queryClient.getQueryData<IInventoryItem>(detailQueryKey);

			if (previousDetailData) {
				queryClient.setQueryData<IInventoryItem>(detailQueryKey, {
					...previousDetailData,
					...updates,
				});
			}

			// Return context for rollback
			return { previousData, previousDetailData, queryKey, detailQueryKey };
		},
		onError: (_error, _variables, context) => {
			// Rollback optimistic updates on error
			if (context) {
				const { previousData, previousDetailData, queryKey, detailQueryKey } = context;

				if (previousData) {
					queryClient.setQueryData(queryKey, previousData);
				}

				if (previousDetailData) {
					queryClient.setQueryData(detailQueryKey, previousDetailData);
				}
			}
		},
		onSuccess: (data: IInventoryItem) => {
			// Invalidate and update the specific inventory item query
			queryClient.setQueryData(queries.inventory.detail(data.id).queryKey, data);
		},
	});
};
